import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Lock, Pencil, Trash2, Plus, LogOut, Package, ClipboardList, Loader2, Image as ImageIcon, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'ile_gourmande_admin_token';

const emptyForm = {
  name: '',
  description: '',
  price: 0,
  category_id: 'cat_maurice',
  image_url: '',
  is_available: true,
  is_featured: false,
  preparation_time: 15,
  allergens: [],
  has_shrimp_addon: false,
  has_formule_option: false,
  has_kids_option: false,
};

const AdminPage = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || '');
  const [password, setPassword] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);
  const [tab, setTab] = useState('menu');
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // null = closed; {} new; {...item} edit
  const [form, setForm] = useState(emptyForm);

  const authHeader = token ? { Authorization: `Bearer ${token}` } : {};

  const handleLogout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken('');
  }, []);

  const loadMenu = useCallback(async () => {
    setLoading(true);
    try {
      const [itemsRes, catsRes] = await Promise.all([
        axios.get(`${API}/admin/menu/items`, { headers: authHeader }),
        axios.get(`${API}/admin/categories`, { headers: authHeader }),
      ]);
      setItems(itemsRes.data);
      setCategories(catsRes.data);
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error('Session expirée, veuillez vous reconnecter');
        handleLogout();
      } else {
        toast.error('Erreur de chargement');
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, handleLogout]);

  const loadOrders = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/orders`, { headers: authHeader });
      setOrders(res.data);
    } catch (err) {
      if (err.response?.status === 401) handleLogout();
      else toast.error('Erreur de chargement commandes');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, handleLogout]);

  useEffect(() => {
    if (!token) return;
    if (tab === 'menu') loadMenu();
    else if (tab === 'orders') loadOrders();
  }, [token, tab, loadMenu, loadOrders]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, res.data.token);
      setToken(res.data.token);
      setPassword('');
      toast.success('Connecté');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Mot de passe incorrect');
    } finally {
      setLoggingIn(false);
    }
  };

  const openCreate = () => {
    setForm(emptyForm);
    setEditing({});
  };

  const openEdit = (item) => {
    setForm({
      name: item.name || '',
      description: item.description || '',
      price: item.price || 0,
      category_id: item.category_id,
      image_url: item.image_url || '',
      is_available: !!item.is_available,
      is_featured: !!item.is_featured,
      preparation_time: item.preparation_time || 15,
      allergens: item.allergens || [],
      has_shrimp_addon: !!item.has_shrimp_addon,
      has_formule_option: !!item.has_formule_option,
      has_kids_option: !!item.has_kids_option,
    });
    setEditing(item);
  };

  const saveItem = async () => {
    try {
      const payload = { ...form, price: parseFloat(form.price), preparation_time: parseInt(form.preparation_time, 10) };
      if (editing.item_id) {
        await axios.put(`${API}/admin/menu/items/${editing.item_id}`, payload, { headers: authHeader });
        toast.success('Plat mis à jour');
      } else {
        await axios.post(`${API}/admin/menu/items`, payload, { headers: authHeader });
        toast.success('Plat créé');
      }
      setEditing(null);
      loadMenu();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur sauvegarde');
    }
  };

  const toggleAvailability = async (item) => {
    try {
      await axios.put(
        `${API}/admin/menu/items/${item.item_id}`,
        { is_available: !item.is_available },
        { headers: authHeader }
      );
      loadMenu();
    } catch (err) {
      toast.error('Erreur mise à jour');
    }
  };

  const deleteItem = async (item) => {
    if (!window.confirm(`Supprimer "${item.name}" ?`)) return;
    try {
      await axios.delete(`${API}/admin/menu/items/${item.item_id}`, { headers: authHeader });
      toast.success('Plat supprimé');
      loadMenu();
    } catch (err) {
      toast.error('Erreur suppression');
    }
  };

  const getCategoryName = (id) => categories.find(c => c.category_id === id)?.name || id;

  const syncFromCode = async () => {
    if (!window.confirm(
      'Récupérer les plats, prix et photos définis dans le code et les appliquer en base ?\n\n' +
      'Les plats du code écraseront les valeurs actuelles. Les plats que vous avez créés manuellement (nouveaux) ne sont pas touchés.'
    )) return;
    try {
      const res = await axios.post(`${API}/admin/sync-menu-from-code`, {}, { headers: authHeader });
      toast.success(`Menu synchronisé : ${res.data.menu_items} plats`);
      loadMenu();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de synchronisation');
    }
  };

  // LOGIN SCREEN
  if (!token) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center px-4" data-testid="admin-login-page">
        <form onSubmit={handleLogin} className="bg-white rounded-2xl shadow-sm p-8 w-full max-w-md space-y-6">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto bg-primary/10 rounded-full flex items-center justify-center mb-4">
              <Lock className="h-7 w-7 text-primary" />
            </div>
            <h1 className="text-2xl font-serif font-bold">Espace Admin</h1>
            <p className="text-sm text-muted-foreground mt-2">L'Île Gourmande</p>
          </div>
          <div>
            <Label htmlFor="admin-password">Mot de passe</Label>
            <Input
              id="admin-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
              data-testid="admin-password-input"
            />
          </div>
          <Button type="submit" className="w-full rounded-full" disabled={loggingIn || !password} data-testid="admin-login-btn">
            {loggingIn && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Se connecter
          </Button>
        </form>
      </div>
    );
  }

  // ADMIN DASHBOARD
  return (
    <div className="min-h-screen bg-stone-50" data-testid="admin-dashboard">
      <header className="bg-white border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-serif font-bold">Espace Admin</h1>
            <p className="text-xs text-muted-foreground">L'Île Gourmande</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="admin-logout-btn">
              <LogOut className="h-4 w-4 mr-1" />
              Déconnexion
            </Button>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 flex gap-1 border-t border-stone-100">
          <button
            onClick={() => setTab('menu')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              tab === 'menu' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            data-testid="tab-menu"
          >
            <Package className="h-4 w-4 inline mr-1" /> Menu ({items.length})
          </button>
          <button
            onClick={() => setTab('orders')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              tab === 'orders' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            data-testid="tab-orders"
          >
            <ClipboardList className="h-4 w-4 inline mr-1" /> Commandes
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {loading && (
          <div className="flex items-center justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        )}

        {tab === 'menu' && !loading && (
          <>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-semibold">Gestion du menu</h2>
              <div className="flex gap-2">
                <Button variant="outline" onClick={syncFromCode} className="rounded-full" data-testid="sync-from-code-btn">
                  <RefreshCw className="h-4 w-4 mr-1" />
                  Sync depuis code
                </Button>
                <Button onClick={openCreate} className="rounded-full" data-testid="create-item-btn">
                  <Plus className="h-4 w-4 mr-1" />
                  Nouveau plat
                </Button>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-stone-100 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-stone-50 border-b border-stone-100">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium">Plat</th>
                    <th className="text-left px-4 py-3 font-medium">Catégorie</th>
                    <th className="text-right px-4 py-3 font-medium">Prix</th>
                    <th className="text-center px-4 py-3 font-medium">Options</th>
                    <th className="text-center px-4 py-3 font-medium">Dispo.</th>
                    <th className="text-right px-4 py-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(item => (
                    <tr key={item.item_id} className="border-b border-stone-100 last:border-0 hover:bg-stone-50/50" data-testid={`admin-row-${item.item_id}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {item.image_url ? (
                            <img src={item.image_url} alt="" className="w-10 h-10 rounded object-cover" />
                          ) : (
                            <div className="w-10 h-10 rounded bg-stone-100 flex items-center justify-center"><ImageIcon className="h-4 w-4 text-stone-400" /></div>
                          )}
                          <div>
                            <p className="font-medium">{item.name}</p>
                            <p className="text-xs text-muted-foreground line-clamp-1">{item.description}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{getCategoryName(item.category_id)}</td>
                      <td className="px-4 py-3 text-right font-semibold">{item.price?.toFixed(2)} €</td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex gap-1 flex-wrap justify-center">
                          {item.has_shrimp_addon && <Badge variant="secondary" className="text-xs">🦐+1€</Badge>}
                          {item.has_formule_option && <Badge variant="secondary" className="text-xs">Formule</Badge>}
                          {item.has_kids_option && <Badge variant="secondary" className="text-xs">Enfant</Badge>}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Switch
                          checked={!!item.is_available}
                          onCheckedChange={() => toggleAvailability(item)}
                          data-testid={`toggle-avail-${item.item_id}`}
                        />
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(item)} data-testid={`edit-${item.item_id}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => deleteItem(item)} className="text-destructive" data-testid={`delete-${item.item_id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {tab === 'orders' && !loading && (
          <>
            <h2 className="text-lg font-semibold mb-6">Historique des commandes ({orders.length})</h2>
            <div className="space-y-3">
              {orders.length === 0 && <p className="text-muted-foreground text-center py-10">Aucune commande.</p>}
              {orders.map(o => (
                <div key={o.order_id} className="bg-white rounded-xl border border-stone-100 p-4" data-testid={`order-row-${o.order_id}`}>
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <p className="font-semibold">#{o.order_id} — {o.customer_name}</p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(o.created_at).toLocaleString('fr-FR')} • {o.customer_phone}
                      </p>
                    </div>
                    <p className="text-lg font-bold text-primary">{o.total?.toFixed(2)} €</p>
                  </div>
                  <p className="text-xs text-muted-foreground mb-2">
                    {o.delivery_address?.street}, {o.delivery_address?.postal_code} {o.delivery_address?.city}
                    {o.desired_time && ` • Heure souhaitée : ${o.desired_time}`}
                  </p>
                  <ul className="text-sm space-y-1">
                    {(o.items || []).map((it, idx) => (
                      <li key={idx}>
                        {it.quantity}x {it.name} — {it.subtotal?.toFixed(2)} €
                        {(it.option_labels || []).map((lbl, i) => (
                          <span key={i} className="block text-xs text-accent ml-4">↳ {lbl}</span>
                        ))}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </>
        )}
      </main>

      {/* Edit/Create dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="edit-item-dialog">
          <DialogHeader>
            <DialogTitle>{editing?.item_id ? 'Modifier le plat' : 'Nouveau plat'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Nom *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="form-name" />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="form-description" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Prix (€) *</Label>
                <Input type="number" step="0.1" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="form-price" />
              </div>
              <div>
                <Label>Catégorie *</Label>
                <Select value={form.category_id} onValueChange={(v) => setForm({ ...form, category_id: v })}>
                  <SelectTrigger data-testid="form-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {categories.map(c => <SelectItem key={c.category_id} value={c.category_id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>URL image</Label>
              <Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="https://..." data-testid="form-image" />
              {form.image_url && <img src={form.image_url} alt="aperçu" className="mt-2 w-24 h-24 rounded object-cover" />}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Temps préparation (min)</Label>
                <Input type="number" value={form.preparation_time} onChange={(e) => setForm({ ...form, preparation_time: e.target.value })} />
              </div>
              <div>
                <Label>Allergènes (séparés par virgules)</Label>
                <Input
                  value={(form.allergens || []).join(', ')}
                  onChange={(e) => setForm({ ...form, allergens: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 pt-2">
              <label className="flex items-center justify-between">
                <span className="text-sm">Disponible</span>
                <Switch checked={form.is_available} onCheckedChange={(v) => setForm({ ...form, is_available: v })} />
              </label>
              <label className="flex items-center justify-between">
                <span className="text-sm">Coup de cœur</span>
                <Switch checked={form.is_featured} onCheckedChange={(v) => setForm({ ...form, is_featured: v })} />
              </label>
              <label className="flex items-center justify-between">
                <span className="text-sm">Option Crevettes (+1€)</span>
                <Switch checked={form.has_shrimp_addon} onCheckedChange={(v) => setForm({ ...form, has_shrimp_addon: v })} data-testid="form-shrimp" />
              </label>
              <label className="flex items-center justify-between">
                <span className="text-sm">Option Formule (+4€)</span>
                <Switch checked={form.has_formule_option} onCheckedChange={(v) => setForm({ ...form, has_formule_option: v })} data-testid="form-formule" />
              </label>
              <label className="flex items-center justify-between">
                <span className="text-sm">Option Enfant (-4€)</span>
                <Switch checked={form.has_kids_option} onCheckedChange={(v) => setForm({ ...form, has_kids_option: v })} data-testid="form-kids" />
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Annuler</Button>
            <Button onClick={saveItem} disabled={!form.name || !form.price} data-testid="save-item-btn">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPage;
